import { useEffect, useState } from "react";
import { applyActionCode } from "firebase/auth";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import { auth } from "../../utilities/firebase";
import { Box, Heading, Text, Button, Spinner, Flex } from "@chakra-ui/react";
import Seo from "../../ui/components/Seo/Seo";

type Status = "loading" | "ok" | "error";

// oobCode одноразовий: кешуємо запит, щоб повторний запуск ефекту
// (StrictMode, ремонтування) не викликав applyActionCode вдруге.
const verifyRequests = new Map<string, Promise<void>>();

const ORANGE = "#EC642B";
const ORANGE_HOVER = "#D9561F";

const CheckIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CrossIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const StatusIcon = ({ ok }: { ok: boolean }) => (
  <Flex
    w="64px"
    h="64px"
    borderRadius="full"
    mx="auto"
    mb="6"
    align="center"
    justify="center"
    bg={ok ? "#E8F5E9" : "#FDECEA"}
    color={ok ? "#2E7D32" : "#D32F2F"}
  >
    {ok ? <CheckIcon /> : <CrossIcon />}
  </Flex>
);

export const VerifyEmailPage = () => {
  const [params] = useSearchParams();
  const [status, setStatus] = useState<Status>("loading");

  const mode = params.get("mode");
  const oobCode = params.get("oobCode");

  useEffect(() => {
    if (mode !== "verifyEmail" || !oobCode) {
      setStatus("error");
      return;
    }

    let request = verifyRequests.get(oobCode);
    if (!request) {
      request = applyActionCode(auth, oobCode);
      verifyRequests.set(oobCode, request);
    }

    let cancelled = false;
    request
      .then(() => {
        if (!cancelled) setStatus("ok");
      })
      .catch((err) => {
        console.error("verify-email error:", err.code, err.message);
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [mode, oobCode]);

  return (
    <>
      <Seo
        description="Підтвердження електронної пошти в Читанці"
        title="Підтвердження пошти"
        noindex
      />
      <Flex
        minH="60vh"
        justify="center"
        align="flex-start"
        px="4"
        py={{ base: "40px", md: "64px" }}
      >
        <Box w="100%" maxW="440px" textAlign="center" color="#1F2430">
          {status === "loading" && (
            <Flex direction="column" align="center" gap="4">
              <Spinner
                w="36px"
                h="36px"
                thickness="3px"
                color={ORANGE}
                emptyColor="#E5E7EB"
              />
              <Text fontSize="md" color="#6B7280">
                Підтверджуємо вашу пошту…
              </Text>
            </Flex>
          )}

          {status === "ok" && (
            <>
              <StatusIcon ok />
              <Heading as="h1" fontSize="32px" fontWeight="800" mb="2">
                Пошту підтверджено
              </Heading>
              <Text fontSize="md" color="#6B7280" lineHeight="1.5" mb="8">
                Тепер можна повернутися в застосунок «Читанка» або перейти на
                головну сторінку.
              </Text>
              <Button
                as={RouterLink}
                to="/"
                w="100%"
                h="52px"
                fontSize="md"
                fontWeight="600"
                color="white"
                bg={ORANGE}
                borderRadius="12px"
                _hover={{ bg: ORANGE_HOVER, textDecoration: "none" }}
                _active={{ bg: ORANGE_HOVER }}
              >
                На головну
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <StatusIcon ok={false} />
              <Heading as="h1" fontSize="32px" fontWeight="800" mb="2">
                Посилання недійсне
              </Heading>
              <Text fontSize="md" color="#6B7280" lineHeight="1.5" mb="8">
                Воно застаріло або вже було використане. Запросіть новий лист у
                застосунку.
              </Text>
              <Button
                as={RouterLink}
                to="/"
                variant="outline"
                w="100%"
                h="52px"
                fontSize="md"
                fontWeight="600"
                color={ORANGE}
                borderColor={ORANGE}
                borderRadius="12px"
                _hover={{ bg: "rgba(236,100,43,.08)", textDecoration: "none" }}
              >
                На головну
              </Button>
            </>
          )}
        </Box>
      </Flex>
    </>
  );
};

export default VerifyEmailPage;